import React, { useEffect, useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import {
  formatTaskAssigneeNames,
  mapTaskStatusToDisplay,
} from '../../../utils/taskDisplay';
import '../styles/ServiceProjectDetailsModal.css';

function fmtDate(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
}

function fmtMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return value || '—';
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 2,
  }).format(n);
}

function fmtStatus(status) {
  if (!status) return '—';
  return String(status)
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function DetailRow({ label, value }) {
  return (
    <div className="spd-detail-row">
      <span>{label}</span>
      <strong>{value ?? '—'}</strong>
    </div>
  );
}

export default function ServiceProjectDetailsModal({ row, onClose }) {
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(true);
  const [tasksError, setTasksError] = useState('');

  useEffect(() => {
    if (!row) return;

    const fetchTasks = async () => {
      setTasksLoading(true);
      setTasksError('');
      try {
        if (row.projectUuid) {
          const data = await graphqlRequest(
            `query FindTasksByProject($projectId: String!) {
              findTasksByProject(projectId: $projectId) {
                id
                title
                description
                location
                date
                time
                priority
                status
                user { id firstName lastName }
                assignees { id firstName lastName }
              }
            }`,
            { projectId: row.projectUuid },
          );
          setTasks(data?.findTasksByProject ?? []);
        } else if (row.bookingId) {
          const data = await graphqlRequest(
            `query FindAllTask($bookingId: String!) {
              findAllTask(bookingId: $bookingId) {
                id
                title
                description
                location
                date
                time
                priority
                status
                user { id firstName lastName }
                assignees { id firstName lastName }
              }
            }`,
            { bookingId: row.bookingId },
          );
          setTasks(data?.findAllTask ?? []);
        } else {
          setTasks([]);
        }
      } catch (err) {
        setTasksError(err?.message || 'Could not load tasks.');
        setTasks([]);
      } finally {
        setTasksLoading(false);
      }
    };

    void fetchTasks();
  }, [row]);

  if (!row) return null;

  const managerName = row.project?.manager
    ? [row.project.manager.firstName, row.project.manager.lastName].filter(Boolean).join(' ')
    : '—';

  return (
    <div className="spd-overlay" onClick={onClose} role="presentation">
      <div
        className="spd-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="spd-modal-title"
      >
        <div className="spd-modal__header">
          <div>
            <h2 id="spd-modal-title">Project &amp; Task Details</h2>
            <p>{row.clientName} · {row.projectId}</p>
          </div>
          <button type="button" className="spd-modal__close" onClick={onClose} aria-label="Close">
            <FaTimes />
          </button>
        </div>

        <div className="spd-modal__body">
          <section className="spd-section">
            <h3>Client Information</h3>
            <div className="spd-grid">
              <DetailRow label="Client Name" value={row.clientName} />
              <DetailRow label="Email" value={row.email} />
              <DetailRow label="Phone" value={row.phone} />
              <DetailRow label="Address" value={row.address} />
              <DetailRow label="Postcode" value={row.postcode} />
              <DetailRow label="Pipeline Stage" value={row.stage} />
              <DetailRow label="Booking Status" value={fmtStatus(row.status)} />
              <DetailRow label="Preferred Date" value={row.preferredDate || '—'} />
            </div>
            {row.message ? (
              <div className="spd-note">
                <span>Client Message</span>
                <p>{row.message}</p>
              </div>
            ) : null}
          </section>

          <section className="spd-section">
            <h3>Service Details</h3>
            <div className="spd-grid">
              <DetailRow label="Service" value={row.serviceName} />
              <DetailRow
                label="Quote Total"
                value={fmtMoney(
                  row.serviceTotal ?? row.servicePrice ?? row.estimatedPrice,
                )}
              />
              <DetailRow label="Frequency" value={row.serviceFrequency || '—'} />
            </div>
          </section>

          <section className="spd-section">
            <h3>Project Details</h3>
            {row.project ? (
              <div className="spd-grid">
                <DetailRow label="Project ID" value={row.projectId} />
                <DetailRow label="Project Name" value={row.project.name} />
                <DetailRow label="Status" value={fmtStatus(row.project.status)} />
                <DetailRow label="Start Date" value={fmtDate(row.project.startDate)} />
                <DetailRow label="Deadline" value={fmtDate(row.project.deadLine)} />
                <DetailRow label="Start Time" value={row.project.startTime || '—'} />
                <DetailRow label="End Time" value={row.project.endTime || '—'} />
                <DetailRow label="Project Manager" value={managerName} />
              </div>
            ) : (
              <p className="spd-empty-inline">No project has been created for this booking yet.</p>
            )}
          </section>

          <section className="spd-section">
            <h3>Tasks</h3>
            {tasksLoading ? (
              <p className="spd-empty-inline">Loading tasks…</p>
            ) : tasksError ? (
              <p className="spd-error">{tasksError}</p>
            ) : tasks.length === 0 ? (
              <p className="spd-empty-inline">No tasks found for this project.</p>
            ) : (
              <div className="spd-tasks-table-wrap">
                <table className="spd-tasks-table">
                  <thead>
                    <tr>
                      <th>Task</th>
                      <th>Assigned To</th>
                      <th>Date</th>
                      <th>Priority</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tasks.map((task) => (
                      <tr key={task.id}>
                        <td>
                          <strong>{task.title}</strong>
                          {task.description ? (
                            <span className="spd-task-desc">{task.description}</span>
                          ) : null}
                          {task.location ? (
                            <span className="spd-task-meta">{task.location}</span>
                          ) : null}
                        </td>
                        <td>
                          {formatTaskAssigneeNames(task)}
                        </td>
                        <td>
                          {task.date || '—'}
                          {task.time ? ` · ${task.time}` : ''}
                        </td>
                        <td>{task.priority || '—'}</td>
                        <td>
                          <span className="spd-status-pill">
                            {mapTaskStatusToDisplay(task.status)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
